// ============================================================================
// client/moine-papier.ts — LES CINQ MOINES EN PAPIER DÉCOUPÉ
// ----------------------------------------------------------------------------
// Lot 5. Les moines partagent UNE silhouette, la capuche, et ne se distinguent
// que par la couleur et un seul attribut : ressort du sauteur, sac du voleur,
// couronne du boss, truelle du bâtisseur. C'est ce qui permet de les reconnaître
// EN FUYANT, ce qu'une différence de forme ne permettrait pas.
//
// Les bulles d'humeur (! ? z) restent dessinées par le jeu (drawMood) : elles
// appartiennent au HUD de comportement, pas au personnage.
//
// Comme le héros, tout passe par un cache borné : le papier est trop cher pour
// être redessiné à chaque image.
// ============================================================================

import { decoupe, papier, rgba } from './papier'

type Ctx = CanvasRenderingContext2D
const OMBRE_TON = '#2e2214'

/** Les cinq types, tels que le moteur les nomme (shared/types.ts MonkKind). */
const TYPES: Record<string, { col: string; ombre: string; attr: string | null }> = {
  normal: { col: '#8b4fd8', ombre: '#3a1e60', attr: null },
  sauteur: { col: '#3fbc80', ombre: '#124a30', attr: 'ressort' },
  voleur: { col: '#e0a020', ombre: '#5a3a08', attr: 'sac' },
  boss: { col: '#c03a3a', ombre: '#4a0e0e', attr: 'couronne' },
  batisseur: { col: '#a08050', ombre: '#4a3a20', attr: 'truelle' },
  /* la STATUE du gong d'or : le même moine, en pierre, sans ornement */
  statue: { col: '#8a8a92', ombre: '#4a4a52', attr: null },
}

function moinePapier(g: Ctx, cx: number, base: number, T: number, col: string, colOmbre: string, attr: string | null): void {
  const u = T / 40
  const capuche = (dx: number, dy: number, fill: string): void => { g.fillStyle = fill; g.beginPath()
    g.moveTo(cx - 13 * u + dx, base + dy); g.lineTo(cx - 10 * u + dx, base - 30 * u + dy)
    g.quadraticCurveTo(cx + dx, base - 42 * u + dy, cx + 10 * u + dx, base - 30 * u + dy)
    g.lineTo(cx + 13 * u + dx, base + dy); g.closePath(); g.fill() }
  capuche(3, 4, rgba(OMBRE_TON, .3))
  g.save(); capuche(0, 0, '#000'); g.clip(); papier(g, 0, 0, g.canvas.width, g.canvas.height, col, 'grain', OMBRE_TON); g.restore()
  g.fillStyle = colOmbre; g.beginPath(); g.ellipse(cx, base - 28 * u, 8 * u, 6.5 * u, 0, 0, 7); g.fill()
  g.fillStyle = '#ffd23f'
  g.beginPath(); g.arc(cx - 3.4 * u, base - 28 * u, 1.9 * u, 0, 7); g.fill()
  g.beginPath(); g.arc(cx + 3.4 * u, base - 28 * u, 1.9 * u, 0, 7); g.fill()
  if (attr === 'ressort') { g.strokeStyle = '#8fe0b0'; g.lineWidth = 2.4 * u; g.lineCap = 'round'
    g.beginPath(); for (let k = 0; k < 3; k++) { g.moveTo(cx - 7 * u, base - k * 3 * u); g.lineTo(cx + 7 * u, base - (k + .5) * 3 * u) } g.stroke() }
  if (attr === 'sac') { decoupe(g, () => { g.beginPath(); g.ellipse(cx + 13 * u, base - 9 * u, 6 * u, 7 * u, .2, 0, 7) }, '#c8a03a', 'pois') }
  if (attr === 'couronne') { g.fillStyle = '#ffc61f'; g.beginPath()
    g.moveTo(cx - 9 * u, base - 38 * u)
    for (let k = 0; k <= 3; k++) { g.lineTo(cx - 9 * u + k * 6 * u, base - 45 * u); g.lineTo(cx - 6 * u + k * 6 * u, base - 39 * u) }
    g.lineTo(cx + 9 * u, base - 38 * u); g.closePath(); g.fill() }
  if (attr === 'truelle') { g.strokeStyle = '#8a6a34'; g.lineWidth = 2.4 * u
    g.beginPath(); g.moveTo(cx + 11 * u, base - 14 * u); g.lineTo(cx + 18 * u, base - 6 * u); g.stroke()
    decoupe(g, () => { g.beginPath(); g.moveTo(cx + 16 * u, base - 9 * u); g.lineTo(cx + 26 * u, base - 12 * u)
      g.lineTo(cx + 22 * u, base - 1 * u); g.closePath() }, '#b6bcc4', 'plis') }
}

/* ----------------------------------------------------------------- le cache */
const CACHE = new Map<string, HTMLCanvasElement>()
const CACHE_MAX = 40
const MARGE = 0.55            // la truelle et le sac sortent de la case

/** Peint un moine dans une case ts×ts au coin (x, y). Le miroir est à la charge
 *  de l'appelant, comme pour l'ancien blit. */
export function blitMoine(ctx: Ctx, kind: string, ts: number, x: number, y: number): void {
  const T = Math.max(8, Math.round(ts * 0.86))
  const cle = kind + '|' + T
  let cv = CACHE.get(cle)
  if (!cv) {
    const t = TYPES[kind] ?? TYPES.normal!
    const w = Math.ceil(T * (1 + MARGE * 2)), h = Math.ceil(T * (1 + MARGE))
    cv = document.createElement('canvas')
    cv.width = w; cv.height = h
    const g = cv.getContext('2d')
    if (g) moinePapier(g, w / 2, T * (1 + MARGE * 0.5), T, t.col, t.ombre, t.attr)
    if (CACHE.size >= CACHE_MAX) { const k = CACHE.keys().next().value; if (k) CACHE.delete(k) }
    CACHE.set(cle, cv)
  }
  ctx.drawImage(cv, x + ts / 2 - cv.width / 2, y + ts - T * (1 + MARGE * 0.5))
}
